/**
 * ReprocessForm (M10, RF-M10-004) — formulário de disparo de reprocessamento.
 *
 * O admin escolhe o MODO (ADR-035, design §8): "Conferir de novo" (padrão — revalida a
 * leitura guardada de cada visita, sem nova leitura e sem custo, sem campo de versão) ou
 * "Ler de novo com a versão…" (o de sempre: versão de prompt alvo obrigatória). E o
 * CRITÉRIO: por FILTRO (UF/município/marcador), por LISTA de IDs de visita ou — só no
 * conferir de novo — TODAS as visitas (emenda da task 11: ler de novo o acervo inteiro tem
 * custo e passaria do limite do evento). O feedback de erro (403 sem permissão / 422 sem
 * alvo) é exibido inline (Req 10 AC6). Nenhum dado pessoal trafega (RN-M11-001).
 */

import { FormEvent, useState } from 'react';
import { Marcador, CriarReprocessamentoInput, ModoReprocessamento } from '../services/painel-types';

export interface ReprocessFormProps {
  readonly enviando: boolean;
  readonly onSubmit: (input: CriarReprocessamentoInput) => void;
}

const MARCADORES: readonly Marcador[] = ['OK', 'CORRIGIDO', 'ALERTA', 'ERRO'];
type Criterio = 'filtro' | 'ids' | 'todas';

export function ReprocessForm({ enviando, onSubmit }: ReprocessFormProps): JSX.Element {
  const [modo, setModo] = useState<ModoReprocessamento>('revalidar');
  const [criterio, setCriterio] = useState<Criterio>('filtro');
  const [versaoPrompt, setVersaoPrompt] = useState('');
  const [uf, setUf] = useState('');
  const [municipioIbge, setMunicipioIbge] = useState('');
  const [marcador, setMarcador] = useState<'' | Marcador>('');
  const [idsTexto, setIdsTexto] = useState('');
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  function escolherModo(novo: ModoReprocessamento): void {
    setModo(novo);
    // "Todas as visitas" só existe no conferir de novo.
    if (novo === 'reler' && criterio === 'todas') {
      setCriterio('filtro');
    }
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    setErroLocal(null);
    const versao = versaoPrompt.trim();
    if (modo === 'reler' && versao.length === 0) {
      setErroLocal('Informe a versão de prompt alvo.');
      return;
    }
    const base: CriarReprocessamentoInput = modo === 'reler' ? { modo, versaoPrompt: versao } : { modo };
    if (criterio === 'ids') {
      const ids = idsTexto
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (ids.length === 0) {
        setErroLocal('Informe ao menos um ID de visita.');
        return;
      }
      onSubmit({ ...base, visitaIds: ids });
      return;
    }
    if (criterio === 'todas') {
      onSubmit({ ...base, filtro: {} });
      return;
    }
    const filtro: CriarReprocessamentoInput['filtro'] = {};
    if (uf.trim()) Object.assign(filtro, { uf: uf.trim().toUpperCase() });
    if (municipioIbge.trim()) Object.assign(filtro, { municipioIbge: municipioIbge.trim() });
    if (marcador) Object.assign(filtro, { marcador });
    onSubmit({ ...base, filtro });
  }

  return (
    <form className="card" onSubmit={handleSubmit} aria-label="Disparar reprocessamento">
      <h2>Novo reprocessamento</h2>

      <div className="field">
        <label>Como</label>
        <div className="row-actions">
          <label>
            <input
              type="radio"
              name="modoReprocessamento"
              checked={modo === 'revalidar'}
              onChange={() => escolherModo('revalidar')}
            />{' '}
            Conferir de novo (sem nova leitura, sem custo)
          </label>
          <label>
            <input
              type="radio"
              name="modoReprocessamento"
              checked={modo === 'reler'}
              onChange={() => escolherModo('reler')}
            />{' '}
            Ler de novo com a versão…
          </label>
        </div>
        <p className="muted">
          {modo === 'revalidar'
            ? 'Aplica as regras e bases de hoje à leitura já guardada de cada visita, na versão em que ela foi lida.'
            : 'Lê as imagens de novo com a versão escolhida (tem custo de extração).'}
        </p>
      </div>

      {modo === 'reler' && (
        <div className="field">
          <label htmlFor="versaoPrompt">Versão de prompt alvo</label>
          <input
            id="versaoPrompt"
            type="text"
            value={versaoPrompt}
            onChange={(e) => setVersaoPrompt(e.target.value)}
            placeholder="ex.: anexo03-v0.3"
          />
        </div>
      )}

      <div className="field">
        <label>Critério</label>
        <div className="row-actions">
          {modo === 'revalidar' && (
            <label>
              <input
                type="radio"
                name="criterio"
                checked={criterio === 'todas'}
                onChange={() => setCriterio('todas')}
              />{' '}
              Todas as visitas
            </label>
          )}
          <label>
            <input
              type="radio"
              name="criterio"
              checked={criterio === 'filtro'}
              onChange={() => setCriterio('filtro')}
            />{' '}
            Por filtro
          </label>
          <label>
            <input
              type="radio"
              name="criterio"
              checked={criterio === 'ids'}
              onChange={() => setCriterio('ids')}
            />{' '}
            Por lista de IDs
          </label>
        </div>
      </div>

      {criterio === 'filtro' && (
        <div className="filtros">
          <div className="field">
            <label htmlFor="uf">UF</label>
            <input id="uf" type="text" maxLength={2} value={uf} onChange={(e) => setUf(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="municipioIbge">Município (IBGE)</label>
            <input
              id="municipioIbge"
              type="text"
              value={municipioIbge}
              onChange={(e) => setMunicipioIbge(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="marcador">Marcador</label>
            <select
              id="marcador"
              value={marcador}
              onChange={(e) => setMarcador(e.target.value as '' | Marcador)}
            >
              <option value="">(qualquer)</option>
              {MARCADORES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {criterio === 'ids' && (
        <div className="field">
          <label htmlFor="ids">IDs de visita (separados por vírgula, espaço ou linha)</label>
          <textarea
            id="ids"
            className="input"
            rows={4}
            value={idsTexto}
            onChange={(e) => setIdsTexto(e.target.value)}
          />
        </div>
      )}

      {erroLocal && <p className="notice">{erroLocal}</p>}

      <div className="row-actions">
        <button className="btn btn-primary" type="submit" disabled={enviando}>
          {enviando ? 'Disparando…' : 'Disparar reprocessamento'}
        </button>
      </div>
    </form>
  );
}
